// ChatInterface.tsx
import React, { useState, useEffect, useRef } from 'react';
import { trustedLoginUrls } from "./loginLinkModel";
import { Send, Loader, User, Bot, AlertCircle, CheckCircle, Upload, File } from 'lucide-react';
import { PayablesActionCard } from './PayablesActionCard';
import { type ActionButtonResponse, type ActionCard, type ButtonKind, parseActionPreview } from './payablesActionViewModel';
import { QUICK_REPLIES, parseQuickReplies } from './quickRepliesModel';

interface Message {
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string;
  timestamp: string;
  toolCalls?: any[];
  /** Story SF-CHAT-5.3: liga a mensagem ao grupo de eventos daquele turno. */
  turnId?: string;
  /** Story SF-CHAT-8.2: cartão de prévia de ação, quando o turno trouxe um. */
  actionCard?: ActionCard;
  /** Stories SF-CHAT-9.4/9.5: ids de respostas rápidas (conjunto fechado). */
  quickReplies?: string[];
}

/**
 * O `detail` do FastAPI é string em erro de negócio e **lista de objetos** em
 * erro de validação (422). Concatenar a lista rende "[object Object]" — erro
 * que não nomeia problema nem recuperação.
 */
function describeDetail(detail: unknown, status: number): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const campos = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'loc' in item) {
          const loc = (item as { loc?: unknown }).loc;
          return Array.isArray(loc) ? String(loc[loc.length - 1]) : null;
        }
        return null;
      })
      .filter((campo): campo is string => Boolean(campo));
    if (campos.length > 0) {
      return status === 422
        ? `A conversa perdeu dados obrigatórios (${campos.join(', ')}). Recarregue a página para começar de novo.`
        : campos.join(', ');
    }
  }
  return `Não foi possível concluir a solicitação (HTTP ${status}).`;
}

interface ChatInterfaceProps {
  sessionId: string;
  /** Segundo fator do turno, emitido junto da sessão. Sem ele o session_id
   *  sozinho seria credencial portadora. */
  correlationId: string;
  /** Falha fatal de inicialização: derruba a superfície. */
  onError?: (error: string) => void;
  /** Falha de um turno: transitória, a conversa continua. */
  onTurnError?: (error: string) => void;
  /** Turno destacado no painel de eventos; realça a mensagem correspondente. */
  selectedTurnId?: string | null;
  /** Clicar numa mensagem seleciona o turno dela no painel (AC #3 da 5.3). */
  onSelectTurn?: (turnId: string | null) => void;
  allowFileUpload?: boolean;
  /** Story SF-CHAT-6.5 (live): grant novo do app para **este** turno. Se falhar,
   *  a mensagem volta para o campo e nada é enviado. */
  requestTurnGrant?: () => Promise<string>;
  /** Veredito público do turno (`turn_result.result_status`). */
  /** Devolve `true` quando o texto do turno deve voltar ao campo (não foi atendido). */
  onTurnVerdict?: (status: string) => boolean | void;
  /** Rodapé do modo vigente. Em live o padrão de fixture seria mentira. */
  footerText?: string;
  /** Story SF-CHAT-8.2: botões do cartão de prévia (grant novo por clique). */
  onAction?: (kind: ButtonKind, previewId: string) => Promise<ActionButtonResponse>;
  /** Story SF-CHAT-9.5: abre o seletor de empresa (grant novo). Sem ele, o botão não aparece. */
  onSwitchCompany?: () => void;
  /** Revisão da 9.5: avisa quando há turno em andamento (a troca espera). */
  onBusyChange?: (busy: boolean) => void;
}

export function ChatInterface({ sessionId, correlationId, onError, onTurnError, selectedTurnId = null, onSelectTurn, allowFileUpload = true, requestTurnGrant, onTurnVerdict, footerText, onAction, onSwitchCompany, onBusyChange }: ChatInterfaceProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pendingLoginTokens, setPendingLoginTokens] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    onBusyChange?.(isLoading);
  }, [isLoading, onBusyChange]);

  // Auto-scroll para última mensagem
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Função para extrair token de URL de login
  const extractLoginToken = (url: string): string | null => {
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('token');
    } catch {
      // Tenta extrair via regex se não for URL válida
      const match = url.match(/[?&]token=([^&\s]+)/);
      return match ? match[1] : null;
    }
  };

  // Detecta links de login nas mensagens
  useEffect(() => {
    const tokens = new Set<string>();
    messages.forEach(msg => {
      if (msg.role === 'assistant' && msg.content) {
        // Procura por URLs de login — só da origem do próprio app (Story 9.2).
        const matches = trustedLoginUrls(msg.content, window.location.origin);
        if (matches.length > 0) {
          matches.forEach(url => {
            const token = extractLoginToken(url);
            if (token) {
              tokens.add(token);
            }
          });
        }
      }
    });
    setPendingLoginTokens(tokens);
  }, [messages]);

  // Polling para verificar status de login
  useEffect(() => {
    if (pendingLoginTokens.size === 0) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Inicia polling
    pollingIntervalRef.current = setInterval(async () => {
      for (const token of pendingLoginTokens) {
        try {
          const response = await fetch(`/api/auth/login-status?token=${token}`);
          const data = await response.json();

          if (data.status === 'completed') {
            // Login concluído!
            setPendingLoginTokens(prev => {
              const next = new Set(prev);
              next.delete(token);
              return next;
            });

            // Adiciona mensagem de sucesso
            setMessages(prev => {
              // Verifica se já existe mensagem de sucesso para este token
              const hasSuccessMsg = prev.some(
                m => m.role === 'system' && m.content.includes('Login realizado com sucesso')
              );
              if (hasSuccessMsg) return prev;

              return [
                ...prev,
                {
                  role: 'system',
                  content: '✅ Login realizado com sucesso! Você está autenticado.',
                  timestamp: new Date().toISOString()
                }
              ];
            });
          } else if (data.status === 'expired' || data.status === 'not_found') {
            // Remove token expirado
            setPendingLoginTokens(prev => {
              const next = new Set(prev);
              next.delete(token);
              return next;
            });
          }
        } catch (error) {
          console.warn('Erro ao verificar status de login:', error);
        }
      }
    }, 2000); // Polling a cada 2 segundos

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [pendingLoginTokens]);

  // Ajusta altura do textarea
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 150) + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [input]);

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove o prefixo "data:application/pdf;base64," se existir
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // `texto`: resposta rápida (Story 9.4) — envia o texto fixo do botão, sem anexo
  // e sem mexer no que a pessoa está digitando.
  const sendMessage = async (texto?: string) => {
    const rapido = typeof texto === 'string';
    if (rapido ? !texto.trim() || isLoading : (!input.trim() && !selectedFile) || isLoading) return;

    let userMessage = rapido ? texto.trim() : input.trim();
    const file = rapido ? null : selectedFile;
    if (!rapido) {
      setInput('');
      setSelectedFile(null);
    }
    setIsLoading(true);
    setIsStreaming(true);

    // Se há arquivo, processa o boleto
    if (file) {
      try {
        const base64File = await convertFileToBase64(file);
        
        // Adiciona mensagem do usuário indicando upload
        const newUserMessage: Message = {
          role: 'user',
          content: userMessage || `Enviando boleto: ${file.name}`,
          timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, newUserMessage]);

        // Envia mensagem com arquivo para processar boleto
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            session_id: sessionId,
            correlation_id: correlationId,
            message: userMessage || `Por favor, processe este boleto: ${file.name}`,
            file: {
              name: file.name,
              type: file.type,
              content: base64File
            },
            stream: true
          }),
        });

        if (!response.ok) {
          const text = await response.text();
          let detail = `HTTP ${response.status}`;
          try {
            const j = JSON.parse(text) as { detail?: unknown };
            if (j.detail != null) {
              detail = describeDetail(j.detail, response.status);
            }
          } catch {
            if (text) detail = text;
          }
          throw new Error(detail);
        }

        // Processa streaming (mesmo código abaixo)
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let assistantMessage = '';
        let tempToolCalls: any[] = [];

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                
                if (data === '[DONE]') {
                  continue;
                }

                try {
                  const parsed = JSON.parse(data);

                  if (parsed.type === 'text_chunk') {
                    assistantMessage += parsed.content;
                    
                    setMessages(prev => {
                      const lastMsg = prev[prev.length - 1];
                      if (lastMsg && lastMsg.role === 'assistant') {
                        return [
                          ...prev.slice(0, -1),
                          { ...lastMsg, content: assistantMessage }
                        ];
                      } else {
                        return [
                          ...prev,
                          {
                            role: 'assistant',
                            content: assistantMessage,
                            timestamp: new Date().toISOString()
                          }
                        ];
                      }
                    });
                  } else if (parsed.type === 'text') {
                    assistantMessage = parsed.content;
                  } else if (parsed.type === 'tool_call') {
                    tempToolCalls.push(parsed);
                    
                    if (parsed.status === 'executing') {
                      setMessages(prev => [
                        ...prev.filter(m => m.role !== 'system' || !m.content.includes('executando')),
                        {
                          role: 'system',
                          content: `🔧 Executando: ${parsed.tool}...`,
                          timestamp: new Date().toISOString()
                        }
                      ]);
                    } else if (parsed.status === 'completed') {
                      setMessages(prev => 
                        prev.filter(m => m.role !== 'system' || !m.content.includes('Executando'))
                      );
                    }
                  } else if (parsed.type === 'error') {
                    throw new Error(parsed.error);
                  }
                } catch (e) {
                  console.warn('Erro ao parsear chunk:', e);
                }
              }
            }
          }
        }

        if (assistantMessage) {
          setMessages(prev => {
            const lastMsg = prev[prev.length - 1];
            if (lastMsg && lastMsg.role === 'assistant') {
              return prev;
            }
            return [
              ...prev,
              {
                role: 'assistant',
                content: assistantMessage,
                timestamp: new Date().toISOString(),
                toolCalls: tempToolCalls.length > 0 ? tempToolCalls : undefined
              }
            ];
          });
        }

        setIsLoading(false);
        setIsStreaming(false);
        return;
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
        onError?.(errorMsg);
        
        setMessages(prev => [...prev, {
          role: 'system',
          content: `❌ Erro ao processar arquivo: ${errorMsg}`,
          timestamp: new Date().toISOString()
        }]);
        setIsLoading(false);
        setIsStreaming(false);
        return;
      }
    }

    // Story SF-CHAT-6.5: em live, o grant do turno vem antes de tudo. Sem ele
    // não há turno — e o texto digitado não pode se perder.
    let turnGrant: string | undefined;
    if (requestTurnGrant) {
      try {
        turnGrant = await requestTurnGrant();
      } catch (grantError) {
        // Revisão da 9.4: atalho não sobrescreve o rascunho que está no campo.
        if (!rapido) setInput(userMessage);
        setIsLoading(false);
        setIsStreaming(false);
        onTurnError?.(grantError instanceof Error ? grantError.message : 'Não foi possível confirmar seu acesso.');
        return;
      }
    }

    // Fluxo normal de mensagem de texto
    const newUserMessage: Message = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          correlation_id: correlationId,
          message: userMessage,
          stream: true,
          ...(turnGrant ? { grant: turnGrant } : {})
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        let detail = `HTTP ${response.status}`;
        try {
          const j = JSON.parse(text) as { detail?: unknown };
          if (j.detail != null) {
            detail = describeDetail(j.detail, response.status);
          }
        } catch {
          if (text) detail = text;
        }
        throw new Error(detail);
      }

      // Processa streaming
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';
      let turnoDesteTurno: string | undefined;
      let cartaoDesteTurno: ActionCard | undefined;
      let botoesDesteTurno: string[] = [];
      let tempToolCalls: any[] = [];

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              
              if (data === '[DONE]') {
                continue;
              }

              try {
                const parsed = JSON.parse(data);

                if (parsed.type === 'text_chunk') {
                  assistantMessage += parsed.content;
                  
                  // Atualiza mensagem em tempo real
                  setMessages(prev => {
                    const lastMsg = prev[prev.length - 1];
                    if (lastMsg && lastMsg.role === 'assistant') {
                      return [
                        ...prev.slice(0, -1),
                        { ...lastMsg, content: assistantMessage }
                      ];
                    } else {
                      return [
                        ...prev,
                        {
                          role: 'assistant',
                          content: assistantMessage,
                          timestamp: new Date().toISOString()
                        }
                      ];
                    }
                  });
                } else if (parsed.type === 'text') {
                  // Mensagem completa
                  assistantMessage = parsed.content;
                } else if (parsed.type === 'tool_call') {
                  tempToolCalls.push(parsed);
                  
                  // Mostra indicador de ferramenta
                  if (parsed.status === 'executing') {
                    setMessages(prev => [
                      ...prev.filter(m => m.role !== 'system' || !m.content.includes('executando')),
                      {
                        role: 'system',
                        content: `🔧 Executando: ${parsed.tool}...`,
                        timestamp: new Date().toISOString()
                      }
                    ]);
                  } else if (parsed.status === 'completed') {
                    // Remove indicador de execução
                    setMessages(prev => 
                      prev.filter(m => m.role !== 'system' || !m.content.includes('Executando'))
                    );
                  }
                } else if (parsed.type === 'action_preview') {
                  // Story 8.2: o cartão só aparece se o chunk bate com o contrato.
                  cartaoDesteTurno = parseActionPreview(parsed) ?? undefined;
                } else if (parsed.type === 'quick_replies') {
                  botoesDesteTurno = parseQuickReplies(parsed);
                } else if (parsed.type === 'turn_result') {
                  // O veredito do turno era emitido desde a Story 4.1 e o
                  // frontend o ignorava. Agora ele traz a identidade do turno,
                  // que é o que liga esta mensagem ao grupo de eventos.
                  if (typeof parsed.turn_id === 'string') {
                    turnoDesteTurno = parsed.turn_id;
                  }
                  if (typeof parsed.result_status === 'string') {
                    if (onTurnVerdict?.(parsed.result_status) === true && !rapido) {
                      setInput(userMessage);
                    }
                  }
                } else if (parsed.type === 'error') {
                  throw new Error(parsed.error);
                }
              } catch (e) {
                console.warn('Erro ao parsear chunk:', e);
              }
            }
          }
        }
      }

      // Finaliza a mensagem do assistente e carimba o turno no par.
      //
      // O carimbo tem de vir **depois** de garantir a mensagem, numa atualização
      // só: o `text_chunk` já adiciona a resposta durante o streaming, então o
      // bloco de finalização retornava cedo e o `turnId` nunca era aplicado — e o
      // par pergunta/resposta ficava sem identidade, com o clique de correlação
      // sem efeito. Visto na tela, não no código.
      if (assistantMessage || turnoDesteTurno || cartaoDesteTurno || botoesDesteTurno.length > 0) {
        setMessages(prev => {
          const jaTemResposta = prev[prev.length - 1]?.role === 'assistant';
          const comResposta = jaTemResposta || !assistantMessage
            ? prev
            : [
                ...prev,
                {
                  role: 'assistant' as const,
                  content: assistantMessage,
                  timestamp: new Date().toISOString(),
                  toolCalls: tempToolCalls.length > 0 ? tempToolCalls : undefined
                }
              ];
          const ultima = comResposta.map(m => m.role).lastIndexOf('assistant');
          const comCartao = (cartaoDesteTurno || botoesDesteTurno.length > 0) && ultima !== -1
            ? comResposta.map((m, i) => (i === ultima
              ? {
                  ...m,
                  ...(cartaoDesteTurno ? { actionCard: cartaoDesteTurno } : {}),
                  ...(botoesDesteTurno.length > 0 ? { quickReplies: botoesDesteTurno } : {}),
                }
              : m))
            : comResposta;
          if (!turnoDesteTurno) return comCartao;

          // A identidade vale para o par: a última resposta e a última pergunta
          // antes dela. São os dois pontos de onde se quer saltar para os eventos.
          const ultimaResposta = comCartao.map(m => m.role).lastIndexOf('assistant');
          const ultimaPergunta = comCartao
            .slice(0, ultimaResposta === -1 ? undefined : ultimaResposta)
            .map(m => m.role)
            .lastIndexOf('user');
          return comCartao.map((m, i) =>
            i === ultimaResposta || i === ultimaPergunta ? { ...m, turnId: turnoDesteTurno } : m
          );
        });
      }

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
      // Erro de turno é transitório: fica na conversa, não derruba a sessão.
      onTurnError?.(errorMsg);

      setMessages(prev => [...prev, {
        role: 'system',
        content: `Não foi possível enviar: ${errorMsg}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
      // O botão de atalho some com a resposta: o foco volta ao campo, não ao body.
      if (rapido) textareaRef.current?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Verifica se é PDF
      if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
        onError?.('Por favor, selecione um arquivo PDF.');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
            <Bot className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-white font-semibold text-lg">SeuFinanceiro Assistant</h2>
            <p className="text-blue-100 text-xs">Assistente financeiro inteligente</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span role="status" aria-live="polite" className="flex items-center space-x-1 text-white text-xs">
            {isStreaming && (
              <>
                <Loader className="w-3 h-3 animate-spin" aria-hidden="true" />
                <span>Digitando...</span>
              </>
            )}
          </span>
          <div className="w-2 h-2 bg-green-400 rounded-full" title="Online"></div>
        </div>
      </div>

      {/* Messages — região viva: a resposta do assistente é anunciada a leitor de tela */}
      <div
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
        tabIndex={0}
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
        aria-label="Conversa"
      >
        {messages.length === 0 && (
          <div className="text-center py-12">
            <Bot className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-gray-500 font-medium mb-2">Olá! 👋</h3>
            <p className="text-gray-400 text-sm max-w-md mx-auto">
              Sou seu assistente financeiro. Posso ajudar você a consultar empresas,
              verificar saldos e muito mais. Como posso ajudar?
            </p>
            <div className="mt-6 space-y-2">
              <button
                onClick={() => setInput('Quais são minhas empresas?')}
                className="block w-full max-w-xs mx-auto px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm text-gray-700"
              >
                💼 Listar minhas empresas
              </button>
              <button
                onClick={() => setInput('Fazer login')}
                className="block w-full max-w-xs mx-auto px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm text-gray-700"
              >
                🔐 Fazer login
              </button>
            </div>
          </div>
        )}

        {messages.map((message, index) => {
          const turnoSelecionado = !!message.turnId && message.turnId === selectedTurnId;
          return (
          <div
            key={index}
            data-turno={message.turnId}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`flex space-x-2 ${message.actionCard ? 'w-full max-w-2xl' : 'max-w-[80%]'} ${
                message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
              }`}
            >
              {/* Avatar */}
              {message.role !== 'system' && (
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  message.role === 'user' 
                    ? 'bg-blue-600' 
                    : 'bg-gray-300'
                }`}>
                  {message.role === 'user' ? (
                    <User className="w-4 h-4 text-white" />
                  ) : (
                    <Bot className="w-4 h-4 text-gray-700" />
                  )}
                </div>
              )}

              {/* Message Content */}
              <div className={`flex flex-col ${message.actionCard ? 'min-w-0 flex-1' : ''}`}>
                <div
                  {...(message.turnId && onSelectTurn
                    ? {
                        role: 'button' as const,
                        tabIndex: 0,
                        'aria-pressed': turnoSelecionado,
                        'aria-label': turnoSelecionado
                          ? 'Mensagem selecionada. Limpar o destaque dos eventos deste turno'
                          : 'Destacar os eventos deste turno no painel',
                        onClick: () => onSelectTurn(turnoSelecionado ? null : message.turnId!),
                        onKeyDown: (evento: React.KeyboardEvent) => {
                          if (evento.key === 'Enter' || evento.key === ' ') {
                            evento.preventDefault();
                            onSelectTurn(turnoSelecionado ? null : message.turnId!);
                          }
                        },
                      }
                    : {})}
                  className={`rounded-lg px-4 py-2 ${
                    message.turnId && onSelectTurn
                      ? 'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700'
                      : ''
                  } ${
                    turnoSelecionado ? 'ring-2 ring-blue-700 ring-offset-1' : ''
                  } ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : message.role === 'system'
                      ? 'bg-yellow-50 text-yellow-800 border border-yellow-200 text-sm'
                      : 'bg-white text-gray-800 border border-gray-200'
                  }`}
                >
                  {/* Renderiza markdown simples e links de login */}
                  <div className="whitespace-pre-wrap break-words">
                    {message.content.split('\n').map((line, i) => {
                      // Botão de login só para URL da origem do próprio app (Story 9.2):
                      // link induzido numa resposta de LLM fica como texto comum.
                      const trusted = trustedLoginUrls(line, window.location.origin);
                      const parts = trusted.length === 0
                        ? [line]
                        : line.split(new RegExp(`(${trusted.map((u) => u.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`));
                      
                      return (
                        <React.Fragment key={i}>
                          {parts.map((part, j) => {
                            const isLoginUrl = trusted.includes(part);
                            if (isLoginUrl) {
                              // É um link de login - usa URL exata para não quebrar
                              const token = extractLoginToken(part);
                              const isPending = token ? pendingLoginTokens.has(token) : false;
                              
                              return (
                                <a
                                  key={j}
                                  href={part}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-block mt-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 font-medium text-sm transition-all shadow-md hover:shadow-lg"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    window.location.href = part;
                                  }}
                                >
                                  🔗 {isPending ? 'Fazer Login (aguardando...)' : 'Clique aqui para fazer login'}
                                </a>
                              );
                            }
                            
                            // Texto normal com formatação markdown
                            return (
                              <React.Fragment key={j}>
                                {part.split('**').map((textPart, k) => 
                                  k % 2 === 0 ? textPart : <strong key={k}>{textPart}</strong>
                                )}
                              </React.Fragment>
                            );
                          })}
                          {i < message.content.split('\n').length - 1 && <br />}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Tool calls indicator */}
                  {message.toolCalls && message.toolCalls.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <CheckCircle className="w-3 h-3" />
                        <span>
                          Executou {message.toolCalls.length} ferramenta(s)
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                {message.actionCard && onAction && (
                  <PayablesActionCard key={message.actionCard.previewId} card={message.actionCard} onAction={onAction} />
                )}
                {/* Stories 9.4/9.5: atalhos só na última resposta, enquanto ela é a última. */}
                {index === messages.length - 1 && message.role === 'assistant' && message.quickReplies && (() => {
                  const botoes = message.quickReplies.filter((id) =>
                    QUICK_REPLIES[id].kind === 'message' || onSwitchCompany !== undefined);
                  if (botoes.length === 0) return null;
                  return (
                    <div role="group" aria-label="Atalhos de resposta" className="mt-2 flex flex-wrap gap-2">
                      {botoes.map((id) => {
                        const r = QUICK_REPLIES[id];
                        return (
                          <button
                            key={id}
                            type="button"
                            disabled={isLoading}
                            onClick={() => (r.kind === 'message' ? sendMessage(r.message) : onSwitchCompany?.())}
                            className="min-h-[44px] rounded-full border border-blue-700 bg-white px-4 py-2 text-sm font-medium text-blue-800 transition-colors hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-500"
                          >
                            {r.label}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}
                <span className={`text-xs text-gray-400 mt-1 ${
                  message.role === 'user' ? 'text-right' : ''
                }`}>
                  {formatTime(message.timestamp)}
                </span>
              </div>
            </div>
          </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 p-4 bg-white">
        {/* Arquivo selecionado */}
        {allowFileUpload && selectedFile && (
          <div className="mb-2 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <div className="flex items-center space-x-2">
              <File className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-800">{selectedFile.name}</span>
            </div>
            <button
              onClick={() => setSelectedFile(null)}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              ✕
            </button>
          </div>
        )}
        
        <div className="flex space-x-2">
          {allowFileUpload && <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={handleFileUploadClick}
              disabled={isLoading}
              className="flex-shrink-0 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Enviar boleto (PDF)"
            >
              <Upload className="w-5 h-5 text-gray-600" />
            </button>
          </>}
          <textarea
            aria-label="Mensagem para o assistente"
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Digite sua mensagem... (Enter para enviar, Shift+Enter para nova linha)"
            className="flex-1 resize-none border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
            rows={1}
            style={{ minHeight: '42px', maxHeight: '150px' }}
          />
          <button
            aria-label="Enviar mensagem"
            onClick={() => sendMessage()}
            disabled={isLoading || (!input.trim() && !selectedFile)}
            className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium transition-colors ${
              isLoading || (!input.trim() && !selectedFile)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isLoading ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center">
          {footerText ?? (allowFileUpload ? 'Orquestrador Python • OpenAI • Streaming Ativo' : 'Modo fixture local • resposta simulada • sem modelo externo')}
        </p>
      </div>
    </div>
  );
}

export default ChatInterface;

